import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, Mail, Phone, Instagram, Lightbulb, Music, Users, Wrench, Monitor, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portfolio = () => {
  const [profileData, setProfileData] = useState(null);
  const [technicalSkills, setTechnicalSkills] = useState({});
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [profileRes, skillsRes, productionsRes] = await Promise.all([
        axios.get(`${API}/profile`),
        axios.get(`${API}/skills`),
        axios.get(`${API}/productions`)
      ]);

      setProfileData(profileRes.data);
      
      // Transform skills array into object with categories as keys
      const skillsObj = {};
      skillsRes.data.forEach(skill => {
        skillsObj[skill.category] = skill.skills;
      });
      setTechnicalSkills(skillsObj);
      
      setProductions(productionsRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load portfolio data');
      setLoading(false);
    }
  };

  const skillCategories = [
    { title: 'Lighting', icon: Lightbulb, category: 'lighting', color: 'from-purple-500/20 to-blue-500/20' },
    { title: 'Audio', icon: Music, category: 'audio', color: 'from-blue-500/20 to-cyan-500/20' },
    { title: 'Management', icon: Users, category: 'management', color: 'from-purple-500/20 to-pink-500/20' },
    { title: 'Stage Operations', icon: Wrench, category: 'stageOps', color: 'from-cyan-500/20 to-blue-500/20' },
    { title: 'Software', icon: Monitor, category: 'software', color: 'from-blue-500/20 to-purple-500/20' }
  ];

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to direct link
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg">{error}</p>
          <Button 
            onClick={fetchAllData} 
            className="mt-4 bg-purple-600 hover:bg-purple-700"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!profileData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${profileData.coverPhoto})`,
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/30 via-blue-900/20 to-slate-950" />
        
        <div className="relative z-10 text-center px-4">
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tight">
            {profileData.name}
          </h1>
          <p className="text-2xl md:text-3xl text-blue-200 mb-6 font-light">
            {profileData.role}
          </p>
          <p className="text-lg text-blue-300 mb-8">
            {profileData.location}
          </p>
          <div className="flex gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-purple-600 hover:bg-purple-700 text-white transition-all"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get In Touch
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-blue-400 text-blue-300 hover:bg-blue-900/30 transition-all"
              onClick={() => document.getElementById('productions').scrollIntoView({ behavior: 'smooth' })}
            >
              View Work
            </Button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-blue-400 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-blue-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About Me
            </h2>
            <p className="text-lg text-slate-300 mb-6 leading-relaxed">
              {profileData.about}
            </p>
            <div className="space-y-4">
              {profileData.education.map((edu, index) => (
                <div key={index} className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-xl font-semibold text-white">{edu.school}</h3>
                  <p className="text-blue-300">{edu.degree}</p>
                  <p className="text-slate-400 text-sm">{edu.year}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="aspect-square rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_theater-portfolio-1/artifacts/5xzd2odf_IMG_5940.jpeg"
                alt="Carter Rohr - Professional headshot"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-gradient-to-br from-purple-600/30 to-blue-600/30 rounded-lg blur-2xl -z-10" />
          </div>
        </div>
      </section>

      {/* Technical Skills Section */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
            Technical Expertise
          </h2>
          
          {/* Lighting Photo 1 - Stage Rigging */}
          <div className="mb-12 group relative overflow-hidden rounded-lg shadow-2xl hover:shadow-blue-500/20 transition-all duration-300">
            <img 
              src="https://customer-assets.emergentagent.com/job_theater-portfolio-1/artifacts/zocof5ra_IMG_6404.jpeg"
              alt="Theater stage with blue lighting and rigging setup"
              className="w-full h-[350px] object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <h3 className="text-xl font-semibold mb-2">Stage Rigging & Lighting</h3>
              <p className="text-blue-200 text-sm">Complete overhead rig with blue atmospheric lighting design</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skillCategories.map((category, index) => {
              const Icon = category.icon;
              const skills = technicalSkills[category.category] || [];
              return (
                <Card key={index} className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700 hover:border-purple-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/10">
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center mb-3`}>
                      <Icon className="w-6 h-6 text-blue-300" />
                    </div>
                    <CardTitle className="text-white">{category.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill, idx) => (
                        <Badge 
                          key={idx} 
                          variant="outline" 
                          className="bg-slate-800/50 text-blue-200 border-slate-600 hover:border-purple-400 transition-colors"
                        >
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Atmospheric Lighting Photo */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="group relative overflow-hidden rounded-lg shadow-2xl hover:shadow-cyan-500/20 transition-all duration-300">
            <img 
              src="https://customer-assets.emergentagent.com/job_theater-portfolio-1/artifacts/ypx6088l_IMG_6374.jpeg"
              alt="Dramatic cyan lighting design with atmospheric effects"
              className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <h3 className="text-xl font-semibold mb-2">Atmospheric Lighting Design</h3>
              <p className="text-cyan-200 text-sm">Dramatic cyan wash with haze effects and silhouette staging</p>
            </div>
          </div>
        </div>
      </section>

      {/* Productions Section */}
      <section id="productions" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-12 text-center">
            Featured Productions
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {productions.map((production) => (
              <Card key={production.id} className="bg-slate-800/60 border-slate-700 hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 group">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge className="bg-purple-600/80 text-white border-0">
                      {production.role}
                    </Badge>
                  </div>
                  <CardTitle className="text-white group-hover:text-blue-300 transition-colors">
                    {production.title}
                  </CardTitle>
                  <CardDescription className="text-blue-200">
                    {production.venue} • {production.date}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {production.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Downloads Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-900/20 via-slate-900/50 to-blue-900/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Download Portfolio Materials
          </h2>
          <p className="text-slate-300 text-lg mb-12">
            Access my complete resume and technical theater portfolio
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-800/80 border-slate-700 hover:border-purple-500 transition-all group">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Theatrical Resume</CardTitle>
                <CardDescription className="text-slate-300">
                  Summary of production history and technical skills
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white transition-all group-hover:scale-105"
                  onClick={() => handleDownload(`${API}/download/resume`, 'Carter_Rohr_Resume.pdf')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Resume
                </Button>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/80 border-slate-700 hover:border-blue-500 transition-all group">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Technical Portfolio</CardTitle>
                <CardDescription className="text-slate-300">
                  Detailed showcase of lighting, sound, and stage work
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-all group-hover:scale-105"
                  onClick={() => handleDownload(`${API}/download/portfolio`, 'Carter_Rohr_Portfolio.pdf')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Portfolio
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-12">
            Get In Touch
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <a 
              href={`mailto:${profileData.email}`}
              className="flex flex-col items-center gap-3 p-6 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-purple-500 transition-all hover:scale-105 group"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center group-hover:from-purple-500/30 group-hover:to-blue-500/30 transition-all">
                <Mail className="w-8 h-8 text-purple-300" />
              </div>
              <span className="text-white font-medium">Email</span>
              <span className="text-blue-300 text-sm">{profileData.email}</span>
            </a>
            <a 
              href={`tel:${profileData.phone}`}
              className="flex flex-col items-center gap-3 p-6 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-blue-500 transition-all hover:scale-105 group"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-all">
                <Phone className="w-8 h-8 text-blue-300" />
              </div>
              <span className="text-white font-medium">Phone</span>
              <span className="text-blue-300 text-sm">{profileData.phone}</span>
            </a>
            <a 
              href={`https://instagram.com/${profileData.instagram.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 p-6 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-purple-500 transition-all hover:scale-105 group"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-all">
                <Instagram className="w-8 h-8 text-purple-300" />
              </div>
              <span className="text-white font-medium">Instagram</span>
              <span className="text-blue-300 text-sm">{profileData.instagram}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-400">
            © 2025 {profileData.name}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;
